import React, { useState, useCallback } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  Tooltip,
} from "@heroui/react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useTranslation } from "react-i18next";

import { DOCXViewer, DocumentInfo } from "@/components/shared/DOCXViewer";
import { logger } from "@/telemetry";

export interface WordPreviewModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Word document data as ArrayBuffer */
  wordData: ArrayBuffer | null;
  /** Title for the modal header */
  title?: string;
  /** Whether the document is currently being generated */
  isLoading?: boolean;
  /** Optional download handler */
  onDownload?: () => void;
}

/**
 * Modal component for previewing Word documents using docx-preview library
 * Renders DOCX files as HTML with proper formatting, headers, footers, and footnotes
 */
// Zoom configuration
const ZOOM_MIN = 50;
const ZOOM_MAX = 200;
const ZOOM_STEP = 10;
const ZOOM_DEFAULT = 100;

export const WordPreviewModal: React.FC<WordPreviewModalProps> = ({
  isOpen,
  onClose,
  wordData,
  title,
  isLoading = false,
  onDownload,
}) => {
  const { t } = useTranslation();

  const [isDownloading, setIsDownloading] = useState(false);
  const [documentInfo, setDocumentInfo] = useState<DocumentInfo | null>(null);
  const [zoomLevel, setZoomLevel] = useState(ZOOM_DEFAULT);

  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    setZoomLevel((prev) => Math.min(prev + ZOOM_STEP, ZOOM_MAX));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel((prev) => Math.max(prev - ZOOM_STEP, ZOOM_MIN));
  }, []);

  const handleResetZoom = useCallback(() => {
    setZoomLevel(ZOOM_DEFAULT);
  }, []);

  // Handle document info from DOCXViewer
  const handleDocumentInfo = useCallback((info: DocumentInfo) => {
    setDocumentInfo(info);
  }, []);

  // Handle download using traditional link method (appears in browser download history)
  const handleDownload = useCallback(async () => {
    if (!wordData || isDownloading) return;

    try {
      setIsDownloading(true);

      // Generate default filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-").split("T");
      const dateStr = timestamp[0];
      const timeStr = timestamp[1].substring(0, 8);
      const baseTitle = title || t("document");
      const sanitizedTitle = baseTitle
        .replace(/[/\\]/g, "") // Remove path separators
        .replace(/\.\./g, "") // Remove parent directory references
        .toLowerCase()
        .replace(/[^a-z0-9\s_-]/g, "_") // Replace disallowed characters
        .replace(/\s+/g, "_") // Replace spaces with underscores
        .replace(/_+/g, "_") // Collapse multiple underscores
        .substring(0, 100); // Limit length
      const defaultFilename = `${sanitizedTitle}_${dateStr}_${timeStr}.docx`;

      // Create blob from ArrayBuffer
      const blob = new Blob([wordData], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      // Use traditional download method so it appears in browser download history
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = defaultFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Call the optional onDownload callback if provided (for analytics/tracking)
      if (onDownload) {
        await onDownload();
      }
    } catch (error) {
      logger.error("Failed to download Word document", error);
    } finally {
      setIsDownloading(false);
    }
  }, [wordData, isDownloading, title, t, onDownload]);

  return (
    <Modal
      classNames={{
        base: "max-h-[95vh] w-[95vw] sm:w-[90vw] max-w-5xl",
        body: "p-0",
      }}
      isOpen={isOpen}
      size="5xl"
      onClose={onClose}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex items-center gap-2 border-b border-divider pr-12">
              <Icon icon="vscode-icons:file-type-word" width={24} />
              <span className="truncate">{title || t("Word Preview")}</span>
              {documentInfo && (
                <span className="text-xs text-default-400 ml-auto whitespace-nowrap">
                  {t("{{count}} words", { count: documentInfo.wordCount })}
                </span>
              )}
            </ModalHeader>
            <ModalBody className="p-0 min-h-[60vh] bg-content1">
              {/* Loading state while document is being generated */}
              {isLoading && (
                <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
                  <Spinner label={t("Generating document...")} size="lg" />
                </div>
              )}

              {/* Empty state when no word data */}
              {!wordData && !isLoading && (
                <div className="flex items-center justify-center h-[60vh]">
                  <p className="text-default-500">{t("No document data available")}</p>
                </div>
              )}

              {/* DOCX Viewer - renders document as HTML */}
              {wordData && !isLoading && (
                <div className="w-full h-[75vh] overflow-auto bg-content1">
                  <DOCXViewer
                    className="w-full bg-content1 p-4 sm:p-8"
                    documentData={wordData}
                    errorMessage={t("Failed to render document")}
                    loadingLabel={t("Loading preview...")}
                    style={{
                      // Use CSS zoom for better layout behaviour (content reflows)
                      zoom: zoomLevel / 100,
                      // Fallback for Firefox which doesn't support zoom
                      MozTransform: `scale(${zoomLevel / 100})`,
                      MozTransformOrigin: "top center",
                      transition: "zoom 0.2s ease-out, transform 0.2s ease-out",
                    }}
                    onDocumentInfo={handleDocumentInfo}
                  />
                </div>
              )}
            </ModalBody>
            <ModalFooter className="justify-between gap-3 border-t border-divider">
              {/* Left spacer for balance */}
              <div className="w-30" />

              {/* Centre zoom controls */}
              <div className="flex items-center gap-2">
                <Tooltip content={t("Zoom out")}>
                  <Button
                    isIconOnly
                    aria-label={t("Zoom out")}
                    isDisabled={zoomLevel <= ZOOM_MIN}
                    radius="sm"
                    size="sm"
                    variant="light"
                    onPress={handleZoomOut}
                  >
                    <Icon icon="solar:minus-circle-linear" width={18} />
                  </Button>
                </Tooltip>

                <Tooltip content={t("Reset zoom")}>
                  <Button
                    aria-label={t("Reset zoom")}
                    className="min-w-15 text-xs font-medium"
                    radius="sm"
                    size="sm"
                    variant="flat"
                    onPress={handleResetZoom}
                  >
                    {zoomLevel}%
                  </Button>
                </Tooltip>

                <Tooltip content={t("Zoom in")}>
                  <Button
                    isIconOnly
                    aria-label={t("Zoom in")}
                    isDisabled={zoomLevel >= ZOOM_MAX}
                    radius="sm"
                    size="sm"
                    variant="light"
                    onPress={handleZoomIn}
                  >
                    <Icon icon="solar:add-circle-linear" width={18} />
                  </Button>
                </Tooltip>
              </div>

              {/* Right download button - styled like PDF download */}
              {onDownload ? (
                <Button
                  aria-label={t("Download")}
                  className="min-w-30 btn-brand-primary"
                  isDisabled={isDownloading || !wordData}
                  radius="md"
                  size="sm"
                  startContent={
                    isDownloading ? (
                      <Spinner className="text-background" size="sm" />
                    ) : (
                      <Icon icon="solar:download-bold" width={18} />
                    )
                  }
                  onPress={handleDownload}
                >
                  {isDownloading ? t("Downloading...") : t("Download")}
                </Button>
              ) : (
                <div className="w-30" />
              )}
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default WordPreviewModal;
